import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiUpload, FiTrash2, FiImage, FiSearch } from 'react-icons/fi';
import { productsAPI, categoriesAPI, subCategoriesAPI } from '../../services/api';

const ProductForm = ({ product, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [formData, setFormData] = useState({
    item_code: '',
    name: '',
    category_id: '',
    sub_category_id: '',
    description: '',
    unit: 'PCS',
    price: '',
    tax_rate: '18.00',
    low_stock_threshold: '10',
    low_stock_alert_enabled: false,
    status: 'active'
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // Searchable category/sub-category dropdown state
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [subCategorySearch, setSubCategorySearch] = useState('');
  const [showSubCategoryDropdown, setShowSubCategoryDropdown] = useState(false);
  const subCategoryDropdownRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (product) {
      setFormData({
        item_code: product.item_code || '',
        name: product.name || '',
        category_id: product.category_id || '',
        sub_category_id: product.sub_category_id || '',
        description: product.description || '',
        unit: product.unit || 'PCS',
        price: product.price || '',
        tax_rate: product.tax_rate || '18.00',
        low_stock_threshold: product.low_stock_threshold || '10',
        low_stock_alert_enabled: Boolean(product.low_stock_alert_enabled),
        status: product.status || 'active'
      });

      if (product.photo) {
        setPhotoPreview(`${process.env.REACT_APP_API_URL.replace('/api', '')}${product.photo}`);
      }

      if (product.category_id) {
        fetchSubCategories(product.category_id);
      }
    }
  }, [product]);

  // Sync the category search box with the selected category's name
  useEffect(() => {
    const selected = categories.find(cat => cat.id === formData.category_id);
    setCategorySearch(selected?.name || '');
  }, [categories, formData.category_id]);

  // Sync the sub-category search box with the selected sub-category's name
  useEffect(() => {
    const selected = subCategories.find(subCat => subCat.id === formData.sub_category_id);
    setSubCategorySearch(selected?.name || '');
  }, [subCategories, formData.sub_category_id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (subCategoryDropdownRef.current && !subCategoryDropdownRef.current.contains(event.target)) {
        setShowSubCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getActive();
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await subCategoriesAPI.getByCategory(categoryId);
      setSubCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      setSubCategories([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handleCategorySelect = (category) => {
    setFormData(prev => ({ ...prev, category_id: category.id, sub_category_id: '' }));
    setCategorySearch(category.name);
    setShowCategoryDropdown(false);
    fetchSubCategories(category.id);
  };

  const handleClearCategory = () => {
    setFormData(prev => ({ ...prev, category_id: '', sub_category_id: '' }));
    setCategorySearch('');
    setSubCategories([]);
    setShowCategoryDropdown(true);
  };

  const filteredSubCategories = subCategories.filter(subCat =>
    subCat.name.toLowerCase().includes(subCategorySearch.toLowerCase())
  );

  const handleSubCategorySelect = (subCategory) => {
    setFormData(prev => ({ ...prev, sub_category_id: subCategory.id }));
    setSubCategorySearch(subCategory.name);
    setShowSubCategoryDropdown(false);
  };

  const handleClearSubCategory = () => {
    setFormData(prev => ({ ...prev, sub_category_id: '' }));
    setSubCategorySearch('');
    setShowSubCategoryDropdown(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (15MB for product images)
      if (file.size > 15 * 1024 * 1024) {
        toast.error('File size must be less than 15MB');
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = async () => {
    if (product && product.photo && !photoFile) {
      // Delete existing photo from server
      if (!window.confirm('Are you sure you want to delete this photo?')) {
        return;
      }

      try {
        await productsAPI.deletePhoto(product.id);
        toast.success('Photo deleted successfully');
        setPhotoPreview(null);
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    } else {
      // Remove preview
      setPhotoFile(null);
      setPhotoPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.unit) {
      toast.error('Unit is required');
      return;
    }
    if (!formData.price || parseFloat(formData.price) < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      setLoading(true);
      let productId;

      if (product) {
        await productsAPI.update(product.id, formData);
        productId = product.id;
        toast.success('Product updated successfully');
      } else {
        const response = await productsAPI.create(formData);
        productId = response.data.data.id;
        toast.success('Product created successfully');
      }

      // Upload photo if selected
      if (photoFile && productId) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        await productsAPI.uploadPhoto(productId, photoFormData);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            {product ? 'Edit Product' : 'Add Product'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6" style={{height:"70vh",overflowY:"auto"}}>
          <div className="space-y-4">
            {/* Photo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Photo
              </label>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {photoPreview ? (
                    <div className="relative">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="h-32 w-32 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="h-32 w-32 bg-gray-100 rounded border flex items-center justify-center">
                      <FiImage className="text-gray-400" size={40} />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <FiUpload /> Upload Photo
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Supported: JPG, PNG, WEBP (Max 15MB)
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Item Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Code
                </label>
                <input
                  type="text"
                  name="item_code"
                  value={formData.item_code}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Auto-generated if empty"
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter product name"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={categoryDropdownRef}>
                  {formData.category_id ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-4 py-2 border border-green-300 bg-green-50 rounded-lg text-sm truncate">
                        {categorySearch}
                      </div>
                      <button
                        type="button"
                        onClick={handleClearCategory}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        title="Change category"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={(e) => {
                          setCategorySearch(e.target.value);
                          setShowCategoryDropdown(true);
                        }}
                        onFocus={() => setShowCategoryDropdown(true)}
                        placeholder="Search category..."
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={!formData.category_id}
                      />
                    </div>
                  )}
                  {showCategoryDropdown && !formData.category_id && (
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredCategories.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">No categories found</div>
                      ) : (
                        filteredCategories.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleCategorySelect(cat)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                          >
                            {cat.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Sub-Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub-Category
                </label>
                <div className="relative" ref={subCategoryDropdownRef}>
                  {formData.sub_category_id ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-4 py-2 border border-green-300 bg-green-50 rounded-lg text-sm truncate">
                        {subCategorySearch}
                      </div>
                      <button
                        type="button"
                        onClick={handleClearSubCategory}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        title="Change sub-category"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={subCategorySearch}
                        onChange={(e) => {
                          setSubCategorySearch(e.target.value);
                          setShowSubCategoryDropdown(true);
                        }}
                        onFocus={() => setShowSubCategoryDropdown(true)}
                        placeholder={!formData.category_id ? 'Select a category first' : 'Search sub-category...'}
                        disabled={!formData.category_id || subCategories.length === 0}
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                  )}
                  {showSubCategoryDropdown && !formData.sub_category_id && formData.category_id && subCategories.length > 0 && (
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredSubCategories.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">No sub-categories found</div>
                      ) : (
                        filteredSubCategories.map((subCat) => (
                          <button
                            key={subCat.id}
                            type="button"
                            onClick={() => handleSubCategorySelect(subCat)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                          >
                            {subCat.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit <span className="text-red-500">*</span>
                </label>
                <select
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="PCS">PCS (Pieces)</option>
                  <option value="KG">KG (Kilograms)</option>
                  <option value="LTR">LTR (Liters)</option>
                  <option value="MTR">MTR (Meters)</option>
                  <option value="BOX">BOX</option>
                  <option value="SET">SET</option>
                </select>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Tax Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  name="tax_rate"
                  value={formData.tax_rate}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Low Stock Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Low Stock Threshold
                </label>
                <input
                  type="number"
                  name="low_stock_threshold"
                  value={formData.low_stock_threshold}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Low Stock Popup Alert */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Low Stock Popup Alert
                </label>
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="low_stock_alert_enabled"
                    checked={formData.low_stock_alert_enabled}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Alert when stock drops below 100 pcs</span>
                </label>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter product description (optional)"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : product ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
